// /components/Toolbar.js
import React, { useState } from 'react';
import { LayoutGrid } from 'lucide-react';
import { usePanelContext, PANEL_COMPONENTS } from './contexts/PanelContext';
import { useTheme } from './contexts/ThemeContext';

const Toolbar = ({ 
  onDeleteNote,
  onAddNote,
  onToggleImageUpload,
  onSidebarContentChange,
  activeContent,
  onContainerSwap,
  onSaveNote,
  onOpenSettings,
  onOpenAITest
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const { theme } = useTheme();
  const { 
    changeRightPanelContent, 
    swapPanels, 
    PANEL_COMPONENTS, 
    setMainPanelContent, 
    mainPanelContent,
    isRightPanelVisible,
    toggleRightPanel,
    isLeftPanelVisible,
    toggleLeftPanel
  } = usePanelContext();

  const handleMouseDown = () => {
    setIsPressed(true);
  };

  const handleMouseUp = () => {
    setIsPressed(false);
    onOpenSettings();
  };

  // Inline styles using theme variables
const toolbarStyles = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 16px',
  backgroundColor: theme.bgToolbar || theme.mainPanelHeaderBg,
  borderBottom: `1px solid ${theme.mainPanelBorder}`,
  borderTop: `1px solid ${theme.mainPanelBorder}`,     // Add this line
  borderLeft: `1px solid ${theme.mainPanelBorder}`,    // Add this line  
  borderRight: `1px solid ${theme.mainPanelBorder}`,   // Add this line
  gap: '8px',
  height: '48px',
  position: 'relative',
  zIndex: 1000
};

  const toolbarLeftStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const toolButtonStyles = {
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const toolButtonActiveStyles = {
    ...toolButtonStyles,
    backgroundColor: theme.mainPanelAccent,
    transform: 'scale(1.1)'
  };

  const toolButtonInactiveStyles = {
    ...toolButtonStyles,
    opacity: 0.7
  };

  const toolButtonHoverStyles = {
    backgroundColor: theme.mainPanelButtonHover,
    transform: 'scale(1.05)'
  };

  return (
    <div style={toolbarStyles}>
      {/* Left group - Document management controls */}
      <div style={toolbarLeftStyles}>
        {/* Left Panel Toggle - NEW */}
        <img
          src={isLeftPanelVisible ? '/images/left-panel-on.png' : '/images/left-panel-off.png'}
          alt={isLeftPanelVisible ? 'Hide Left Panel' : 'Show Left Panel'}
          style={toolButtonStyles}
          onClick={toggleLeftPanel}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        
        <img
          src="/images/add-document.png"
          alt="Add Note"
          style={toolButtonStyles}
          onClick={onAddNote}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        
        <img
          src="/images/visual-workspace.png"
          alt="Visual Workspace"
          style={
            mainPanelContent === PANEL_COMPONENTS.MAIN_PANEL.VISUAL_WORKSPACE 
              ? toolButtonActiveStyles 
              : toolButtonInactiveStyles
          }
          onClick={() => {
            setMainPanelContent(
              mainPanelContent === PANEL_COMPONENTS.MAIN_PANEL.VISUAL_WORKSPACE
                ? PANEL_COMPONENTS.MAIN_PANEL.NOTE_VIEW
                : PANEL_COMPONENTS.MAIN_PANEL.VISUAL_WORKSPACE
            );
          }}
          onMouseEnter={(e) => {
            if (mainPanelContent !== PANEL_COMPONENTS.MAIN_PANEL.VISUAL_WORKSPACE) {
              e.target.style.backgroundColor = theme.mainPanelButtonHover;
              e.target.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (mainPanelContent !== PANEL_COMPONENTS.MAIN_PANEL.VISUAL_WORKSPACE) {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }
          }}
        />
        
        <img
          src="/images/remove.png"
          alt="Delete Note"
          style={toolButtonStyles}
          onClick={onDeleteNote}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonDanger;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        
        <img
          src="/images/image-upload.png"
          alt="Toggle Image Upload"
          style={toolButtonStyles}
          onClick={() => onToggleImageUpload()}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        
        <img
          src="/images/settings.png"
          alt="Settings"
          style={toolButtonStyles}
          onClick={onOpenSettings}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        
        <img
          src={mainPanelContent === PANEL_COMPONENTS.MAIN_PANEL.AI_VIEW ? '/images/aion.png' : '/images/ai.png'}
          alt="AI Test"
          style={
            mainPanelContent === PANEL_COMPONENTS.MAIN_PANEL.AI_VIEW 
              ? toolButtonActiveStyles 
              : toolButtonStyles
          }
          onClick={() => setMainPanelContent(
            mainPanelContent === PANEL_COMPONENTS.MAIN_PANEL.AI_VIEW 
              ? PANEL_COMPONENTS.MAIN_PANEL.NOTE_VIEW 
              : PANEL_COMPONENTS.MAIN_PANEL.AI_VIEW
          )}
          onMouseEnter={(e) => {
            if (mainPanelContent !== PANEL_COMPONENTS.MAIN_PANEL.AI_VIEW) {
              e.target.style.backgroundColor = theme.mainPanelButtonHover;
              e.target.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (mainPanelContent !== PANEL_COMPONENTS.MAIN_PANEL.AI_VIEW) {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }
          }}
        />
        
        <img
          src="/images/gallery.png"
          alt="Toggle Gallery"
          style={
            activeContent === 'gallery' 
              ? toolButtonActiveStyles 
              : toolButtonStyles
          }
          onClick={() => {
            onSidebarContentChange('gallery');
            changeRightPanelContent(PANEL_COMPONENTS.RIGHT_PANEL.IMAGE_GALLERY);
          }}
          onMouseEnter={(e) => {
            if (activeContent !== 'gallery') {
              e.target.style.backgroundColor = theme.mainPanelButtonHover;
              e.target.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeContent !== 'gallery') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }
          }}
        />
        
        <img
          src="/images/sticky-notes.png"
          alt="Show Stickies" 
          style={
            activeContent === 'stickies' 
              ? toolButtonActiveStyles 
              : toolButtonInactiveStyles
          }
          onClick={() => {
            onSidebarContentChange('stickies');
            changeRightPanelContent(PANEL_COMPONENTS.RIGHT_PANEL.STICKY_NOTES);
          }}
          onMouseEnter={(e) => {
            if (activeContent !== 'stickies') {
              e.target.style.backgroundColor = theme.mainPanelButtonHover;
              e.target.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeContent !== 'stickies') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }
          }}
        />
        
        <img
          src="/images/threads.png"
          alt="Show Threads"
          style={
            activeContent === 'threads' 
              ? toolButtonActiveStyles 
              : toolButtonInactiveStyles
          }
          onClick={() => {
            onSidebarContentChange('threads');
            changeRightPanelContent(PANEL_COMPONENTS.RIGHT_PANEL.THREADS);
          }}
          onMouseEnter={(e) => {
            if (activeContent !== 'threads') {
              e.target.style.backgroundColor = theme.mainPanelButtonHover;
              e.target.style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeContent !== 'threads') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'scale(1)';
            }
          }}
        />
        
        <img
          src="/images/swap.png"
          alt="Swap Content"
          style={toolButtonStyles}
          onClick={() => {
            console.log('Swap button clicked in Toolbar');
            swapPanels();
            if (onContainerSwap) {
              console.log('Calling onContainerSwap');
              onContainerSwap();
            }
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
        
        {/* Right Panel Toggle */}
        <img
          src={isRightPanelVisible ? '/images/toggle-on.png' : '/images/toggle-off.png'}
          alt={isRightPanelVisible ? 'Hide Right Panel' : 'Show Right Panel'}
          style={toolButtonStyles}
          onClick={toggleRightPanel}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.mainPanelButtonHover;
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.transform = 'scale(1)';
          }}
        />
      </div>
    </div>
  );
};

export default Toolbar;